import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ShieldCheck, RefreshCw, Loader2, Activity, Users, MessageSquare, Newspaper, Eye } from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';
import { FeatureFlagsPanel } from './FeatureFlagsPanel';
import { EmailSettingsPanel } from './EmailSettingsPanel';
import { FlightSettingsPanel } from './FlightSettingsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_META = {
  admin: { label: 'Admin', color: '#00f0ff' },
  paid: { label: 'Paid Members', color: '#ff9e00' },
  regular: { label: 'Regular Users', color: '#39ff14' },
};

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="glass-card rounded-xl border border-slate-800 p-5 space-y-3 hover:border-slate-700 transition-all">
    <div className="flex items-center justify-between">
      <div className="w-9 h-9 rounded-lg border flex items-center justify-center" style={{ background: `${color}15`, borderColor: `${color}40`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <span className="font-mono text-[9px] text-slate-500 uppercase tracking-widest">{label}</span>
    </div>
    <div className="font-display font-extrabold text-3xl text-white tracking-tight">{value ?? '—'}</div>
  </div>
);

export const AdminView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await axios.get(`${API}/analytics`);
      setData(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => { setRefreshing(true); load(); };

  const byRole = data?.users_by_role || {};
  const orderedRoles = ['admin', 'paid', 'regular'];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-10" data-testid="admin-view">
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00f0ff]/10 border border-[#00f0ff]/25 rounded-full">
            <ShieldCheck className="w-3 h-3 text-[#00f0ff] shrink-0" />
            <span className="font-mono text-[10px] text-[#00f0ff] tracking-widest font-semibold uppercase">06 // ADMIN CONSOLE</span>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">
            Admin <span className="text-[#00f0ff]">Telemetry</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed max-w-2xl">
            Live site analytics and user role distribution. Visible only to administrators.
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="px-4 py-2 rounded border border-[#00f0ff]/40 bg-[#00f0ff]/10 text-[#00f0ff] hover:bg-[#00f0ff]/20 disabled:opacity-50 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
          data-testid="btn-admin-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </section>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-rose-300 text-xs font-mono">! {error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500 font-mono text-xs gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading telemetry…
        </div>
      ) : (
        <>
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={Eye} label="Home page loads" value={data?.home_loads} color="#00f0ff" />
            <StatCard icon={Activity} label="Total hits" value={data?.total_hits} color="#39ff14" />
            <StatCard icon={Users} label="Registered users" value={data?.users_count} color="#9d4edd" />
            <StatCard icon={MessageSquare} label="Guestbook posts" value={data?.guestbook_count} color="#ff9e00" />
          </section>

          <section className="glass-card rounded-2xl border border-slate-800 p-6 space-y-5" data-testid="admin-user-groups">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#9d4edd]" />
              <h3 className="font-display font-bold text-sm text-white uppercase tracking-wide">User Groups</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {orderedRoles.map((role) => {
                const meta = ROLE_META[role];
                return (
                  <div key={role} className="rounded-xl border p-5 space-y-2" style={{ background: `${meta.color}08`, borderColor: `${meta.color}40` }}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>{meta.label}</span>
                      <ShieldCheck className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    </div>
                    <div className="font-display font-extrabold text-2xl text-white">{byRole[role] ?? 0}</div>
                  </div>
                );
              })}
            </div>
            <div className="font-mono text-[10px] text-slate-500 border-t border-slate-900/60 pt-3">
              Paid group is reserved for the upcoming subscription module (Stripe integration planned).
            </div>
          </section>

          <section className="glass-card rounded-2xl border border-slate-800 p-6 space-y-3">
            <h3 className="font-display font-bold text-sm text-white uppercase tracking-wide flex items-center gap-2">
              <Newspaper className="w-4 h-4 text-[#39ff14]" />
              Content snapshot
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 font-mono text-xs text-slate-300">
              <div className="flex justify-between border-b border-slate-900/60 pb-2">
                <span className="text-slate-500">Feed articles in ledger</span>
                <span className="text-white">{data?.feed_articles_count}</span>
              </div>
              <div className="flex justify-between border-b border-slate-900/60 pb-2">
                <span className="text-slate-500">Last analytics update</span>
                <span className="text-white">{data?.updated_at ? new Date(data.updated_at).toLocaleString() : '—'}</span>
              </div>
            </div>
          </section>

          <EmailSettingsPanel />
          <FlightSettingsPanel />
          <FeatureFlagsPanel />
        </>
      )}
    </motion.div>
  );
};
